import { IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { PackageTriggerName, PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { TransactionActorClient, TransactionUserClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { useEffect, useState } from 'react';

import { useLicenseRepository } from '../../../utils/license-repository';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    transaction: ReturnType<typeof useWizardTransaction>['transaction'];
    product?: IProduct;
    equipment?: IEquipment;
    slot: Slot | null;
    isProcessing: boolean;
    handleSubmit: () => void;
}

const useReviewDetail = (): ViewProps => {
    const { store, wizardId, navigationState, onSubmit, wizardData } = useScreen();
    const { getFunctionality, getFunctionalitySetting } = useLicenseRepository();
    const auditFunctionality = getFunctionality(Application.LENDING, Feature.AUDIT_TRANSACTION);
    const { product, equipment, stock } = wizardData;
    const { transaction, syncTransaction } = useWizardTransaction({
        wizardId,
        store
    });
    const { localUpdatePackage } = useWizardPackage({
        store: store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [slot, setSlot] = useState<Slot | null>(null);

    useEffect(() => {
        if (transaction && transaction.account_id && transaction.spot_id && transaction.module_id && transaction.slot_id) {
            SlotClient.fetchSlot(transaction.account_id, transaction.spot_id, transaction.module_id, transaction.slot_id).then(setSlot);
        } else if (stock && stock.account_id && stock.spot_id && stock.module_id && stock.slot_id) {
            SlotClient.fetchSlot(stock.account_id, stock.spot_id, stock.module_id, stock.slot_id).then(setSlot);
        }
    }, [stock, transaction]);

    const handleSubmit = async () => {
        if (equipment) {
            await handleEquipmentSubmit();
        } else if (transaction && transaction.type === TransactionType.VENDING) {
            await handleProductSubmit();
        }
    };

    const handleProductSubmit = async () => {
        setIsProcessing(true);
        try {
            const pkg = await PackageClient.fetchAccountTransactionPackages(transaction!.account_id!, transaction!.id!).then((pkgs) =>
                pkgs.results.find((p) => p.product_id === product?.id && p.type === PackageType.AUDIT)
            );
            if (!pkg) throw new Error('Package not found');
            await localUpdatePackage(pkg);

            if (navigationState.user?.id && transaction && transaction.id) {
                TransactionActorClient.fetchTransactionActors({
                    transaction_id: transaction.id,
                    user_id: navigationState.user.id,
                    type: TransactionActorType.PICKED_UP_BY
                }).then((result) => {
                    if (result.results.length === 0) {
                        TransactionActorClient.postTransactionActor(transaction!.account_id!, transaction!.id!, {
                            user_id: navigationState.user!.id,
                            type: TransactionActorType.PICKED_UP_BY
                        });
                    }
                });
            }

            await PackageWorkflowClient.triggerPackageState(pkg.account_id, pkg.transaction_id, pkg.id, {
                trigger: PackageTriggerName.PACKAGE_CONTINUE,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
            onSubmit();
        } finally {
            setIsProcessing(false);
        }
    };

    const handleEquipmentSubmit = async () => {
        if (!auditFunctionality || !stock || !product || !equipment) return;
        const settings = getFunctionalitySetting(auditFunctionality.id);
        const trxWF = settings[SettingKey.TRANSACTION_WORKFLOW_ID] as string | undefined;
        const pkgWF = settings[SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID] as string | undefined;
        if (!trxWF || !pkgWF) return;
        setIsProcessing(true);
        try {
            const trx = await syncTransaction({
                application: Application.LENDING,
                feature: Feature.AUDIT_TRANSACTION,
                state: TransactionStateName.TRANSACTION_CREATED,
                workflow_id: trxWF,
                type: TransactionType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                user_id: navigationState.user?.id || undefined,
                iot_id: navigationState.iot?.id || undefined
            });

            const pkg = await PackageClient.postPackage(trx.account_id, trx.id, {
                workflow_id: pkgWF,
                user_id: trx.user_id,
                quantity: 1,
                type: PackageType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                product_id: product.id,
                equipment_id: equipment.id,
                priority: 1,
                blocking: false,
                application: Application.LENDING,
                feature: Feature.AUDIT_TRANSACTION
            });
            await localUpdatePackage(pkg);
            await TransactionWorkflowClient.triggerTransactionState(trx.account_id, trx.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
            onSubmit();
        } finally {
            setIsProcessing(false);
        }
    };

    return {
        transaction,
        product,
        equipment,
        slot,
        isProcessing,
        handleSubmit
    };
};

export default useReviewDetail;
